import { Button, ButtonGroup } from "@bokio/designsystem/components/Button";
import { CardRadioGroup } from "@bokio/designsystem/components/CardRadioGroup/CardRadioGroup";
import { LoadingProgress } from "@bokio/designsystem/components/Loading";
import { InfoTooltip } from "@bokio/designsystem/components/Tooltip";
import { Div } from "@bokio/designsystem/components/TypographicElements/TypographicElements";
import { AccountingLangFactory, GeneralLangFactory } from "@bokio/lang";
import { useCompanyInfo } from "@bokio/mobile-web-shared/core/contexts/CompanyInfoContext/CompanyInfoContext";
import * as m from "@bokio/mobile-web-shared/core/model/model";
import { useLazyApi } from "@bokio/mobile-web-shared/hooks/useApi/useApi";
import * as proxy from "@bokio/mobile-web-shared/services/api/proxy";
import { useRouter } from "@bokio/shared/containers/router/useRouter";
import { getRoute } from "@bokio/shared/route";
import { useCompanyUser } from "@bokio/shared/state/requests";
import { noop } from "@bokio/shared/utils";

import {
	getBookkeepingEventCategoryOptions,
	getReceiptCategoryOptions,
} from "../../../../utils/bookkeepingCategoryOptions";

import type { CategoryExample, CategoryWithExamples } from "../../../../utils/bookkeepingCategoryOptions";
import type { CardRadioOption } from "@bokio/designsystem/components/CardRadioGroup/CardRadioGroup";
import type { FC, JSX } from "react";

import * as styles from "./bookkeepingCategoryStep.scss";

import ReceiptCategoryType = m.Core.Contracts.Todo.ReceiptCategoryType;

export type BookkeepingCategoryVariant = "receipt" | "bookkeepingEvent";

interface BookkeepingCategoryStepProps {
	category?: string;
	onCategoryChange: (value: string) => void;
	receiptId?: string;
	onSubmit: () => void;
	onSave?: () => void;
	onSaveToUploads?: () => void;
	variant?: BookkeepingCategoryVariant;
	/** When true, shows only a Save button without navigation options */
	saveOnly?: boolean;
	/** Optional warning notice to display above the action buttons */
	warningNotice?: React.ReactNode;
}

const ExamplesTooltipContent: FC<{ examples: CategoryExample[] }> = ({ examples }) => {
	return (
		<div className={styles.tooltipExamplesContainer}>
			{examples.map((example, index) => (
				<div key={index} className={example.isNegative ? styles.exampleNegative : styles.examplePositive}>
					<span className={styles.exampleIcon}>{example.isNegative ? "âœ—" : "âœ“"}</span>
					<span className={styles.exampleText}>{example.text}</span>
				</div>
			))}
		</div>
	);
};

const TitleWithTooltip: FC<{ title: string; examples?: CategoryExample[] }> = ({ title, examples }) => {
	if (!examples || examples.length === 0) {
		return <>{title}</>;
	}
	return (
		<span className={styles.titleWithTooltip}>
			{title}
			<InfoTooltip
				stopPropagation={true}
				tooltipClassName={styles.examplesInfoTooltip}
				contentGenerator={() => <ExamplesTooltipContent examples={examples} />}
			/>
		</span>
	);
};

export const BookkeepingCategoryStep: FC<BookkeepingCategoryStepProps> = ({
	onCategoryChange,
	onSubmit,
	category,
	receiptId,
	onSave,
	onSaveToUploads,
	variant = "receipt",
	saveOnly,
	warningNotice,
}) => {
	const accountingLang = AccountingLangFactory();
	const generalLang = GeneralLangFactory();
	const { companyInfo } = useCompanyInfo();
	const { companyUserPermissions } = useCompanyUser();
	const { redirect } = useRouter();
	const [setReceiptType, request] = useLazyApi(proxy.Accounting.ReceiptController.SetType.Post);

	const categoryOptions =
		variant === "bookkeepingEvent" ? getBookkeepingEventCategoryOptions() : getReceiptCategoryOptions();
	const standardEventsOptions = companyUserPermissions ? categoryOptions.standardEvents : [];
	const specialEventsOptions = companyUserPermissions ? categoryOptions.specialEvents : [];

	// Transform options to include InfoTooltip in the title
	const transformOptionsWithTooltip = (options: CategoryWithExamples[]): CardRadioOption[] => {
		return options.map(option => ({
			...option,
			subItems: option.subItems.map(subItem => ({
				...subItem,
				title: (
					<TitleWithTooltip
						title={typeof subItem.title === "string" ? subItem.title : String(subItem.title)}
						examples={option.examples}
					/>
				),
			})),
		}));
	};

	const handleUpdateReceipt = async (categoryOverride?: string) => {
		const cat = categoryOverride || category;
		if (!receiptId || !cat) {
			return;
		}
		await setReceiptType(companyInfo.Id, {
			ReceiptId: receiptId,
			SelectedType: cat,
		});
	};

	const handleSubmit = async (categoryOverride?: string) => {
		await handleUpdateReceipt(categoryOverride);
		onSubmit();
	};

	const handleSave = async () => {
		await handleUpdateReceipt();
		if (onSave) {
			onSave();
		} else {
			redirect(getRoute("todo", { company: companyInfo.Id }));
		}
	};

	const handleSaveToUploads = async (categoryOverride?: string) => {
		await handleUpdateReceipt(categoryOverride);
		if (onSaveToUploads) {
			onSaveToUploads();
		} else {
			redirect(getRoute("uploads", { company: companyInfo.Id }));
		}
	};

	const shouldNotContinue = (category?: string) => {
		const validCategories = [
			ReceiptCategoryType.supplier,
			ReceiptCategoryType.income,
			ReceiptCategoryType.expense,
			ReceiptCategoryType.other,
		];
		return !validCategories.some(c => c === category);
	};

	const getButtons = () => {
		const buttons: JSX.Element[] = [];

		// When saveOnly is true, show only a simple Save button
		if (saveOnly) {
			buttons.push(
				<Button
					key="save-only"
					testId="BokkeepingCategoryStep_Button_Save"
					onClick={handleSave}
					disabled={!category || request.isLoading}
				>
					{generalLang.Save}
				</Button>,
			);
			return buttons;
		}

		// Receipt mode: show category-specific CTAs
		if (variant === "receipt" && receiptId) {
			if (category === ReceiptCategoryType.supplier && companyUserPermissions?.SupplierInvoices) {
				// Supplier invoice: Categorize and create invoice (primary), Categorize and close
				buttons.push(
					<Button
						key="supplier-create"
						testId="BokkeepingCategoryStep_Button_CategorizeAndCreate"
						onClick={() => handleSubmit()}
						disabled={request.isLoading}
					>
						{accountingLang.Bookkeeping_CategorizeAndCreateInvoice}
					</Button>,
					<Button
						key="supplier-close"
						testId="BokkeepingCategoryStep_Button_CategorizeAndClose"
						appearance="secondary"
						onClick={handleSave}
						disabled={request.isLoading}
					>
						{accountingLang.Bookkeeping_CategorizeAndClose}
					</Button>,
				);
			} else if (category === ReceiptCategoryType.transactionReceipt) {
				// Receipt or cash invoice on synced accounts: Categorize and close (primary)
				buttons.push(
					<Button
						key="transaction-close"
						testId="BokkeepingCategoryStep_Button_CategorizeAndClose"
						onClick={() => handleSaveToUploads()}
						disabled={request.isLoading}
					>
						{accountingLang.Bookkeeping_CategorizeAndClose}
					</Button>,
				);
			} else if (category === ReceiptCategoryType.expense) {
				// Purchase from other account: Categorize and record (primary), Categorize and close
				buttons.push(
					<Button
						key="expense-record"
						testId="BokkeepingCategoryStep_Button_CategorizeAndRecord"
						onClick={() => handleSubmit()}
						disabled={request.isLoading}
					>
						{accountingLang.Bookkeeping_CategorizeAndRecord}
					</Button>,
					<Button
						key="expense-close"
						testId="BokkeepingCategoryStep_Button_CategorizeAndClose"
						appearance="secondary"
						onClick={handleSave}
						disabled={request.isLoading}
					>
						{accountingLang.Bookkeeping_CategorizeAndClose}
					</Button>,
				);
			} else if (category === ReceiptCategoryType.income) {
				// Income from other account: Categorize and record (primary), Categorize and close
				buttons.push(
					<Button
						key="income-record"
						testId="BokkeepingCategoryStep_Button_CategorizeAndRecord"
						onClick={() => handleSubmit()}
						disabled={request.isLoading}
					>
						{accountingLang.Bookkeeping_CategorizeAndRecord}
					</Button>,
					<Button
						key="income-close"
						testId="BokkeepingCategoryStep_Button_CategorizeAndClose"
						appearance="secondary"
						onClick={handleSave}
						disabled={request.isLoading}
					>
						{accountingLang.Bookkeeping_CategorizeAndClose}
					</Button>,
				);
			} else if (category === ReceiptCategoryType.other) {
				// Other: Categorize and record (primary), Categorize and close
				buttons.push(
					<Button
						key="other-record"
						testId="BokkeepingCategoryStep_Button_CategorizeAndRecord"
						onClick={() => handleSubmit()}
						disabled={request.isLoading}
					>
						{accountingLang.Bookkeeping_CategorizeAndRecord}
					</Button>,
					<Button
						key="other-close"
						testId="BokkeepingCategoryStep_Button_CategorizeAndClose"
						appearance="secondary"
						onClick={handleSave}
						disabled={request.isLoading}
					>
						{accountingLang.Bookkeeping_CategorizeAndClose}
					</Button>,
				);
			} else {
				// No category selected: show disabled placeholder
				buttons.push(
					<Button key="placeholder" testId="BokkeepingCategoryStep_Button_Placeholder" disabled onClick={noop}>
						{accountingLang.Bookkeeping_SelectCategory}
					</Button>,
				);
			}
			return buttons;
		}

		// Original logic for non-receipt mode
		if (category === ReceiptCategoryType.supplier && companyUserPermissions?.SupplierInvoices) {
			buttons.push(
				<Button
					key="supplier"
					testId="BokkeepingCategoryStep_Button_Continue"
					onClick={() => handleSubmit()}
					disabled={request.isLoading}
				>
					{accountingLang.BookkeepCategoryStep_NextButton}
				</Button>,
			);
		} else if (category === ReceiptCategoryType.transactionReceipt) {
			buttons.push(
				<Button
					key="save-transaction"
					testId="BokkeepingCategoryStep_Button_SaveTransaction"
					onClick={() => handleSaveToUploads()}
					disabled={request.isLoading}
				>
					{generalLang.Save}
				</Button>,
			);
		} else if (companyUserPermissions?.Bookkeep) {
			buttons.push(
				<Button
					key="bookkeep"
					testId="BokkeepingCategoryStep_Button_Continue"
					onClick={() => handleSubmit()}
					disabled={shouldNotContinue(category) || request.isLoading}
				>
					{accountingLang.BookkeepCategoryStep_NextButton}
				</Button>,
			);
		}

		if (receiptId) {
			buttons.push(
				<Button
					key="save"
					testId="BokkeepingCategoryStep_Button_Save"
					appearance="secondary"
					onClick={handleSave}
					disabled={request.isLoading}
				>
					{accountingLang.Bookkeeping_CategoryStep_SaveAndDoLaterButton}
				</Button>,
			);
		}

		return buttons;
	};

	return (
		<Div>
			<div className={styles.sectionHeader}>{accountingLang.BookkeepCategory_StandardEvents}</div>
			<div className={styles.categoryOption}>
				<CardRadioGroup
					layout="vertical"
					options={transformOptionsWithTooltip(standardEventsOptions)}
					value={category}
					onChange={onCategoryChange}
					name="BookkeepingCategory"
				/>
			</div>

			<div className={styles.sectionHeader}>{accountingLang.BookkeepCategory_SpecialEvents}</div>
			<div className={styles.categoryOption}>
				<CardRadioGroup
					layout="vertical"
					options={transformOptionsWithTooltip(specialEventsOptions)}
					value={category}
					onChange={onCategoryChange}
					name="BookkeepingCategory"
				/>
			</div>

			<LoadingProgress request={request} />
			<div className={variant === "receipt" && receiptId ? styles.stickyButtonContainer : undefined}>
				{warningNotice}
				<ButtonGroup
					direction="column"
					stretch
					margin={variant === "receipt" && receiptId ? undefined : ["top", "bottom"]}
				>
					{getButtons()}
				</ButtonGroup>
			</div>
		</Div>
	);
};
